import { useState, useCallback, useRef, useEffect } from 'react'
import { apiClient } from '@/services/api'
import { useErrorHandler } from '@/hooks/useAuth'

interface UseDownloadQRCodeReturn {
  download: (trolleyId: number, filename?: string) => Promise<void>
  isLoading: boolean
  error: Nullable<string>
}

export function useDownloadQRCode(): UseDownloadQRCodeReturn {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Nullable<string>>(null)
  const { handleError } = useErrorHandler()
  const handleErrorRef = useRef(handleError)

  useEffect(() => {
    handleErrorRef.current = handleError
  }, [handleError])

  const download = useCallback(async (trolleyId: number, filename?: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiClient.get(`/qr-codes/${trolleyId}`, {
        responseType: 'blob',
      })

      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename || `qr-code-${trolleyId}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(handleErrorRef.current(err))
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    download,
    isLoading,
    error,
  }
}
